import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { DatePicker } from 'components/DatePicker';
import { cn } from 'lib/utils';
import useAuthStore from 'store/auth';
import {
  captureUserActivity,
  getFamilyMemberAttributes,
  getMemberDetails,
} from 'services/profile/api';
import { UserAttributeData } from 'services/profile/type';
import { USER_ACTIVITY } from '../../../types';
import { useUpdateFamilyAttributes } from 'services/profile/hooks';
import {
  UserDetailsFormData,
  UserDetailsValidator,
} from 'lib/validators/user-details';

const NUMERIC_REGEX = /^(?:[1-9][0-9]*)?$/;

export default function UserDetails({
  onClose,
  setLoading,
}: {
  onClose: () => void;
  setLoading: (loading: boolean) => void;
}) {
  const { t } = useTranslation();

  const userAttributes = useAuthStore((store) => store.userAttributes);
  const isAdmin = useAuthStore((store) => store.isCurrentUserAdmin);
  const profileId = useAuthStore((store) => store.currentUserProfileId);
  const setUserAttributes = useAuthStore((store) => store.setUserAttributes);
  const setMemberDetails = useAuthStore((store) => store.setMemberDetails);

  const form = useForm<UserDetailsFormData>({
    resolver: zodResolver(UserDetailsValidator),
    defaultValues: {
      birthdate: userAttributes?.birthdate
        ? parse(userAttributes.birthdate, 'dd/MM/yyyy', new Date())
        : undefined,
      gender: userAttributes?.gender,
      given_name: userAttributes?.given_name,
      family_name: userAttributes?.family_name,
      height: userAttributes?.height,
      weight: userAttributes?.weight,
      height_unit: userAttributes?.height_unit,
      weight_unit: userAttributes?.weight_unit,
    },
  });

  const { mutate: updateFamily, isPending: isFamilyPending } =
    useUpdateFamilyAttributes({
      onSuccess: async () => {
        captureUserActivity(
          isAdmin
            ? USER_ACTIVITY.UPDATE_ADMIN_DETAIL
            : USER_ACTIVITY.UPDATE_PROFILE_DETAIL
        );
        const attributes = await getFamilyMemberAttributes({
          profile_id: profileId,
        });
        if (attributes) setUserAttributes(attributes);

        const memberDetails = await getMemberDetails();
        if (memberDetails) setMemberDetails(memberDetails);
        onClose();
      },
      onError: () => toast.error(t('failed_to_update_details_msg')),
      onSettled: () => setLoading(false),
    });

  function onSubmit(values: UserDetailsFormData) {
    setLoading(true);
    const data = {
      ...values,
      birthdate: format(values.birthdate, 'dd/MM/yyyy'),
      middle_name: '',
    };
    updateFamily(data as UserAttributeData);
  }

  return (
    <div className='flex items-center justify-center w-full h-full my-6'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-2xl px-6 mx-auto space-y-6'
        >
          <FormField
            control={form.control}
            name='given_name'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel className='text-gray-300'>
                  {t('firstname')}
                </FormLabel>

                <FormControl>
                  <Input {...field} className='text-gray-300 bg-transparent' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='family_name'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel className='text-gray-300'>{t('lastname')}</FormLabel>

                <FormControl>
                  <Input {...field} className='text-gray-300 bg-transparent' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='birthdate'
            render={({ field }) => (
              <FormItem className='flex flex-col w-full'>
                <FormLabel className='text-gray-300' htmlFor='dob'>
                  {t('dob')}
                </FormLabel>

                <FormControl>
                  <DatePicker
                    selected={field.value}
                    onSelect={(value: Date | undefined) =>
                      field.onChange(value)
                    }
                  >
                    <Button
                      id='dob'
                      variant='ghost'
                      className={cn(
                        'justify-start hover:bg-transparent text-gray-300 hover:text-gray-300 border-b-2 border-b-primary rounded-none h-12 pb-0 relative',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      {field.value ? format(field.value, 'dd/MM/yyyy') : null}
                      <CalendarIcon className='absolute w-5 h-5 right-2' />
                    </Button>
                  </DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='gender'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel className='text-gray-300' htmlFor='gender'>
                  {t('gender')}
                </FormLabel>

                <FormControl>
                  <Select
                    {...field}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger
                      id='gender'
                      className='text-gray-300 bg-transparent border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='male'>{t('male')}</SelectItem>
                      <SelectItem value='female'>{t('female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex items-end'>
            <FormField
              control={form.control}
              name='height'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel className='text-gray-300'>{t('height')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) => {
                        const { value } = e.target;
                        const regex = NUMERIC_REGEX;
                        if (regex.test(value)) {
                          field.onChange(value);
                        }
                      }}
                      className='text-gray-300 bg-transparent'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='height_unit'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      {...field}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className='w-20 text-gray-300 bg-transparent border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='cm'>cm</SelectItem>
                        <SelectItem value='ft'>ft</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex items-end'>
            <FormField
              control={form.control}
              name='weight'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel className='text-gray-300'>{t('weight')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) => {
                        const { value } = e.target;
                        const regex = NUMERIC_REGEX;
                        if (regex.test(value)) {
                          field.onChange(value);
                        }
                      }}
                      className='text-gray-300 bg-transparent'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='weight_unit'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      {...field}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className='w-20 text-gray-300 bg-transparent border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='kg'>kg</SelectItem>
                        <SelectItem value='lbs'>lbs</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex justify-center !mt-8 gap-4'>
            <Button
              type='button'
              onClick={onClose}
              className='bg-rose-400 hover:bg-rose-400/80'
            >
              {t('discard')}
            </Button>
            <Button
              type='submit'
              className='px-5'
              disabled={!form.formState.isValid || isFamilyPending}
            >
              {isFamilyPending && (
                <Loader2
                  role='progressbar'
                  className='inline w-4 h-4 mr-2 animate-spin'
                />
              )}
              {t('save')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
